import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    companyDomain: '',
    industry: '',
    role: 'admin',
    agreeToTerms: false,
    agreeToPrivacy: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);

  const industryOptions = [
    { value: '', label: 'Select your industry', disabled: true },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'technology', label: 'Technology & Software' },
    { value: 'healthcare', label: 'Healthcare & Pharmaceuticals' },
    { value: 'finance', label: 'Finance & Banking' },
    { value: 'education', label: 'Education & Training' },
    { value: 'hospitality', label: 'Hospitality & Tourism' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'other', label: 'Other' }
  ];

  const roleOptions = [
    { 
      value: 'admin', 
      label: 'Admin',
      description: 'Full access to all features, user management, and settings'
    },
    { 
      value: 'member', 
      label: 'Member',
      description: 'Create and edit catalogs, view analytics, limited settings access'
    }
  ];

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password?.length >= 8) score++;
    if (password?.length >= 12) score++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) score++;
    if (/\d/?.test(password)) score++;
    if (/[^a-zA-Z0-9]/?.test(password)) score++;

    const strengthLevels = [
      { score: 0, label: '', color: '' },
      { score: 1, label: 'Weak', color: 'bg-destructive' },
      { score: 2, label: 'Fair', color: 'bg-warning' },
      { score: 3, label: 'Good', color: 'bg-accent' },
      { score: 4, label: 'Strong', color: 'bg-success' },
      { score: 5, label: 'Very Strong', color: 'bg-success' }
    ];

    return strengthLevels?.[score];
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (field === 'password') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = t('auth.register.errors.fullNameRequired');
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = t('auth.register.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = t('auth.register.errors.emailInvalid');
    }

    if (!formData?.password) {
      newErrors.password = t('auth.register.errors.passwordRequired');
    } else if (formData?.password?.length < 8) {
      newErrors.password = t('auth.register.errors.passwordMinLength');
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = t('auth.register.errors.confirmPasswordRequired');
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = t('auth.register.errors.passwordMismatch');
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = t('auth.register.errors.termsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.companyDomain?.trim()) {
      newErrors.companyDomain = 'Company domain is required';
    } else if (!/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/?.test(formData?.companyDomain)) {
      newErrors.companyDomain = 'Please enter a valid domain (e.g., company.com)';
    }

    if (!formData?.industry) {
      newErrors.industry = 'Please select your industry';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms of Service';
    }

    if (!formData?.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'You must agree to the Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNextStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) {
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await signUp(
        formData?.email,
        formData?.password,
        {
          full_name: formData?.fullName,
          company_name: formData?.companyName,
          company_domain: formData?.companyDomain,
          role: formData?.role
        }
      );

      if (error) {
        setErrors({ submit: error?.message });
      } else {
        setShowSuccess(true);
      }
    } catch (error) {
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-4">
          {[1, 2]?.map((step) => (
            <React.Fragment key={step}>
              <div className="flex items-center">
                <div
                  className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-sm md:text-base font-semibold transition-all ${
                    currentStep >= step
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step}
                </div>
                <span
                  className={`ml-2 md:ml-3 text-xs md:text-sm font-medium ${
                    currentStep >= step ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {step === 1 ? 'Personal Info' : 'Company Details'}
                </span>
              </div>
              {step < 2 && (
                <div
                  className={`flex-1 h-0.5 mx-2 md:mx-4 transition-all ${
                    currentStep > step ? 'bg-accent' : 'bg-muted'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        {currentStep === 1 && (
          <>
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData?.fullName}
              onChange={(e) => handleInputChange('fullName', e?.target?.value)}
              error={errors?.fullName}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="you@company.com"
              description="We'll send a verification email to this address"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />

            <div>
              <Input
                label="Password"
                type="password"
                placeholder="Create a strong password"
                value={formData?.password}
                onChange={(e) => handleInputChange('password', e?.target?.value)}
                error={errors?.password}
                required
              />
              {formData?.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Password strength:</span>
                    <span className={`text-xs font-medium ${
                      passwordStrength?.score >= 4 ? 'text-success' :
                      passwordStrength?.score >= 3 ? 'text-accent' :
                      passwordStrength?.score >= 2 ? 'text-warning': 'text-destructive'
                    }`}>
                      {passwordStrength?.label}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5]?.map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          level <= passwordStrength?.score
                            ? passwordStrength?.color
                            : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Input
              label="Confirm Password"
              type="password"
              placeholder="Re-enter your password"
              value={formData?.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
              error={errors?.confirmPassword}
              required
            />

            <div className="pt-2">
              <Checkbox
                label={
                  <span className="text-sm">
                    I agree to the{' '}
                    <a href="#" className="text-accent hover:underline">
                      Terms of Service
                    </a>
                  </span>
                }
                checked={formData?.agreeToTerms}
                onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
                error={errors?.agreeToTerms}
              />
            </div>

            <Button
              type="button"
              onClick={handleNextStep}
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              className="mt-6"
            >
              Continue to Company Details
            </Button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Input
              label="Company Name"
              type="text"
              placeholder="Your company name"
              value={formData?.companyName}
              onChange={(e) => handleInputChange('companyName', e?.target?.value)}
              error={errors?.companyName}
              required
            />

            <Input
              label="Company Domain"
              type="text"
              placeholder="company.com"
              description="Your company's website domain"
              value={formData?.companyDomain}
              onChange={(e) => handleInputChange('companyDomain', e?.target?.value)}
              error={errors?.companyDomain}
              required
            />

            <Select
              label="Industry"
              options={industryOptions}
              value={formData?.industry}
              onChange={(value) => handleInputChange('industry', value)}
              error={errors?.industry}
              required
            />

            <div className="bg-muted/50 rounded-lg p-4 md:p-6 space-y-4">
              <div className="flex items-start">
                <Icon name="Shield" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-foreground mb-1">Select Your Role</h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Choose the role that best fits your responsibilities
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {roleOptions?.map((option) => (
                  <label
                    key={option?.value}
                    className={`flex items-start p-3 md:p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData?.role === option?.value
                        ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={option?.value}
                      checked={formData?.role === option?.value}
                      onChange={(e) => handleInputChange('role', e?.target?.value)}
                      className="mt-1 flex-shrink-0"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-semibold text-foreground">{option?.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">{option?.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <Checkbox
                label={
                  <span className="text-sm">
                    I agree to the{' '}
                    <a href="#" className="text-accent hover:underline">
                      Terms of Service
                    </a>
                  </span>
                }
                checked={formData?.agreeToTerms}
                onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
                error={errors?.agreeToTerms}
              />

              <Checkbox
                label={
                  <span className="text-sm">
                    I agree to the{' '}
                    <a href="#" className="text-accent hover:underline">
                      Privacy Policy
                    </a>
                  </span>
                }
                checked={formData?.agreeToPrivacy}
                onChange={(e) => handleInputChange('agreeToPrivacy', e?.target?.checked)}
                error={errors?.agreeToPrivacy}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handlePreviousStep}
                iconName="ArrowLeft"
                iconPosition="left"
                className="sm:w-auto"
              >
                Back
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                iconName="CheckCircle"
                iconPosition="right"
                fullWidth
                className="sm:flex-1"
              >
                Create Account
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default RegistrationForm;